import { create } from 'zustand';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHICH PRODUCT AN ADMINISTRATOR IS LOOKING AT
 *
 * An administrator holds two jobs: running the organisation, and doing their
 * own training like everybody else. Those want different menus and a different
 * dashboard, and for a while both were on screen at once — the admin sections
 * at the top, their own learning folded into a section at the bottom.
 *
 * This replaces that fold with a switch. In ADMIN view they get Manage,
 * Content and Insights and the organisation dashboard; in EMPLOYEE view they
 * get exactly what an employee gets, which is also the only way to check what
 * their staff actually see.
 *
 * ── It is a VIEW, not a permission ─────────────────────────────────────────
 *
 * Switching changes what is offered, never what is allowed. Every admin route
 * stays reachable by URL in employee view and the API enforces the same rules
 * either way — which is the honest arrangement, because a menu has never been
 * a security control and pretending otherwise is how people start trusting it
 * as one.
 *
 * Nobody but an administrator has two jobs, so nobody else sees the switch.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type ViewMode = 'admin' | 'employee';

const STORAGE_KEY = 'petpooja.viewMode';

/**
 * Remembered across reloads, because switching to employee view to answer
 * "what does my team see on this page" and losing it on every refresh makes
 * the feature useless for the one job it exists for.
 *
 * Wrapped, because `localStorage` throws outright in a private window with
 * site data blocked — and a dashboard that fails to render over a menu
 * preference would be a poor trade.
 */
function readStored(): ViewMode {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'employee' ? 'employee' : 'admin';
  } catch {
    return 'admin';
  }
}

interface ViewModeState {
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
  /** Back to the administrator's own view. Called when a session ends. */
  reset: () => void;
}

export const useViewModeStore = create<ViewModeState>((set) => ({
  mode: readStored(),

  setMode: (mode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Not remembered this time; the switch still works for this session.
    }
    set({ mode });
  },

  /**
   * Cleared on sign-out along with the query cache.
   *
   * Otherwise the next person to sign in on this browser inherits it — and an
   * ordinary employee would never see the switch to put it back, so they would
   * simply be stuck in a view somebody else chose.
   */
  reset: () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear.
    }
    set({ mode: 'admin' });
  },
}));
